import React, { useEffect, useRef, useState, useMemo } from "react";
import { Controller, useWatch } from "react-hook-form";
import Input from "../../../components/FormInputs";
import CustomButton from "../../../components/AddButton";
import FormTitle from "../../../components/FormTitle";
import CustomModal from "../../../components/Modal";
import { MASTER_URL } from "../../../common/common";
import UnitDropDown from "../../../components/Dropdown/unitDropdown";
import { ApiService } from "../../../service/api.service";
import {
  CargoDetail,
  CargoDetailsProps,
} from "../../../service/interfaces/ApiResponse.interface";
import { Plus, cancelButton } from "../../../common/svg";
import SecureImage from "../../../components/BucketImage/Image";

// Deep clone utility to break all references and avoid frozen arrays/objects
function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

const CargoDetails: React.FC<CargoDetailsProps> = ({
  errors,
  register,
  setValue,
  control,
  setCargoDetailsData,
  CargoDetailsData,
  getValues,
}) => {
  const apiService = new ApiService();
  const [forms, setForms] = useState<number[]>([0]);
  const [useSameContact, setUseSameContact] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [formIdToRemove, setFormIdToRemove] = useState<number | null>(null);
  const [cargo, setCargo] = useState<any>([]);
  const [cargoType, setCargoType] = useState<any>([]);
  const [packageType, setPackageType] = useState<any>([]);
  const isMasterDataFetched = useRef(false);
  const isInitialRender = useRef(true);

  const watchFields =
    (useWatch({ control, name: "cargoDetails" }) as CargoDetail[]) || [];

  const memoizedCargoDetails = useMemo(() => {
    return CargoDetailsData || [];
  }, [CargoDetailsData]);

  const getFieldError = (index: number, fieldName: string): string => {
    const cargoDetailsErrors = errors.cargoDetails as
      | Record<string, any>
      | undefined;
    return cargoDetailsErrors?.[index]?.[fieldName]?.message || "";
  };

  const hasFieldError = (index: number, fieldName: string): boolean => {
    const cargoDetailsErrors = errors.cargoDetails as
      | Record<string, any>
      | undefined;
    return !!cargoDetailsErrors?.[index]?.[fieldName];
  };

  const openModal = (formId: number) => {
    setFormIdToRemove(formId);
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handleAddForm = () => {
    setForms((prevForms) => [...prevForms, prevForms.length]);
    setUseSameContact(false);
  };

  // Robust helper for immutable updates using deep clone
  const setCargoDetailField = (index: number, field: string, value: any) => {
    const allValues = getValues() as any;
    const details = Array.isArray(allValues.cargoDetails)
      ? deepClone(allValues.cargoDetails)
      : [];
    while (details.length <= index) details.push({});
    details[index] = { ...details[index], [field]: value };
    setValue("cargoDetails", details);
  };

  const handleConfirmRemove = () => {
    if (formIdToRemove !== null) {
      setForms((prevForms) => prevForms.filter((id) => id !== formIdToRemove));
      // Deep clone to break all references and avoid frozen arrays/objects
      const updatedFields = deepClone(watchFields.filter((_: CargoDetail, idx: number) => idx !== formIdToRemove));
      setValue("cargoDetails", updatedFields);
      setFormIdToRemove(null);
      closeModal();
    }
  };

  const getMasterData = async () => {
    if (isMasterDataFetched.current) return;
    try {
      const response: any = await apiService.getApi(
        `${MASTER_URL}/booking/data-route?key=Booking Type`
      );
      if (response.status === "200") {
        const bookingData = response.body["Booking Type"] || [];
        const cargoTypeData = response.body["Cargo Type"] || [];
        const packageTypeData = response.body["Package Type"] || [];
        setCargo(bookingData.map((item: any) => ({ value: item.metaId, label: item.value })));
        setCargoType(cargoTypeData.map((item: any) => ({ value: item.metaId, label: item.value })));
        setPackageType(packageTypeData.map((item: any) => ({ value: item.metaId, label: item.value })));
        isMasterDataFetched.current = true;
      }
    } catch (error) {
      console.error("Error fetching master data:", error);
    }
  };

  useEffect(() => {
    return () => {
      const currentCargoData = getValues().cargoDetails || [];
      setCargoDetailsData(deepClone(currentCargoData));
    };
  }, []);

  useEffect(() => {
    if (!isInitialRender.current) return;
    getMasterData();
    if (memoizedCargoDetails.length > 0) {
      const formCount = memoizedCargoDetails.length;
      setForms(Array.from({ length: formCount }, (_, i) => i));
      const cargoDetailsCopy = deepClone(memoizedCargoDetails);
      cargoDetailsCopy.forEach((data: CargoDetail, index: number) => {
        Object.entries(data).forEach(([key, value]) => {
          setCargoDetailField(index, key, value);
        });
      });
      isInitialRender.current = false;
    } else if (forms.length === 0) {
      setForms([0]);
      isInitialRender.current = false;
    }
  }, []);

  useEffect(() => {
    if (watchFields && watchFields.some((field) => Object.keys(field).length > 0)) {
      setCargoDetailsData(deepClone(watchFields));
    }
  }, [watchFields, setCargoDetailsData]);

  return (
    <div>
      <div className="bg-white p-4 rounded-[20px]">
        <div className="flex items-center justify-between mb-4">
          <FormTitle title="Cargo Details" />
          <CustomButton label="" onClick={handleAddForm} icon={Plus} />
        </div>
        {forms.map((formId, index) => (
          <div key={formId} className="border-b border-dashed mb-4 pb-4">
            <div className="flex items-center justify-end mb-4 gap-2">
              <p className="text-[14px] font-[600]">
                #{(index + 1).toString().padStart(2, "0")}
              </p>
              {index >= 1 && (
                <button
                  onClick={() => openModal(formId)}
                  type="button"
                  className="bg-red-500 text-white p-1 rounded-full shadow-md hover:bg-red-600 transition-all"
                  aria-label="Remove"
                >
                  <SecureImage fileKey={cancelButton} alt="down arrow" width="15px" />
                </button>
              )}
            </div>
            <div className="grid md:grid-cols-4 gap-4 mb-4">
              <div className=" ">
                <Input
                  label="Cargo "
                  name={`cargoDetails[${index}].cargo`}
                  register={register}
                  errors={errors}
                  // onChange: use Controller if you want to use setCargoDetailField for custom logic
                />
              </div>
              <div className="w-full sm:max-w-[300px]">
                <Input
                  label="Sub Cargo "
                  name={`cargoDetails[${index}].subCargo`}
                  register={register}
                  errors={errors}
                />
              </div>
              <div className="w-full sm:max-w-[300px]">
                <Input
                  label="Cargo Type"
                  name={`cargoDetails[${index}].cargoType`}
                  register={register}
                  errors={errors}
                />
              </div>
            </div>
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="w-full lg:w-1/2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <UnitDropDown
                    label="Cargo Volume"
                    name={`cargoDetails[${index}].volume`}
                    errors={
                      hasFieldError(index, "volume")
                        ? {
                            [`cargoDetails[${index}].volume`]: {
                              message: getFieldError(index, "volume"),
                            },
                          }
                        : {}
                    }
                    placeholder="Enter Cargo Volume"
                    unitOptions={["Unit", "Kg", "Lbs"]}
                    register={register}
                    required
                    selectedValue={memoizedCargoDetails[index]?.volume}
                    selectedUnit={memoizedCargoDetails[index]?.volume_unit}
                  />
                  <UnitDropDown
                    label="Cargo Weight"
                    name={`cargoDetails[${index}].weight`}
                    errors={
                      hasFieldError(index, "weight")
                        ? {
                            [`cargoDetails[${index}].weight`]: {
                              message: getFieldError(index, "weight"),
                            },
                          }
                        : {}
                    }
                    placeholder="Enter Cargo Weight"
                    unitOptions={["Unit", "Kg", "Lbs"]}
                    register={register}
                    required
                    selectedValue={memoizedCargoDetails[index]?.weight}
                    selectedUnit={memoizedCargoDetails[index]?.weight_unit}
                  />
                  <div className="sm:hidden w-full h-2"></div>
                  <Input
                    label="Package Type"
                    name={`cargoDetails[${index}].packageType`}
                    register={register}
                    errors={errors}
                  />
                  <Input
                    label="Remarks"
                    name={`cargoDetails[${index}].remarks`}
                    register={register}
                    errors={errors}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <CustomModal
        isOpen={isModalOpen}
        content={
          <div className="flex flex-col items-center p-6">
            <p className="text-[18px] font-[600] text-[#4B4B4B] text-center">
              Are you sure you want to remove this form?
            </p>
            <div className="mt-6 flex gap-4">
              <button
                onClick={handleConfirmRemove}
                type="button"
                className="bg-red-500 hover:bg-red-600 rounded-[5px] h-[40px] w-[100px] text-white transition-colors"
              >
                Yes
              </button>
              <button
                onClick={closeModal}
                type="button"
                className="bg-gray-500 hover:bg-gray-600 rounded-[5px] h-[40px] w-[100px] text-white transition-colors"
              >
                No
              </button>
            </div>
          </div>
        }
        onClose={closeModal}
      />
    </div>
  );
};

export default CargoDetails;